/**
 * MvbDeclarationsRegister -- register of all MVB declarations (P7.4).
 *
 * Lists declarations grouped by status (drafts on top, approved
 * highlighted, superseded de-emphasised). Admin-only via the
 * existing /v1/admin/foundation/* gate on the backend.
 */
import { formatDate } from "shared/time/formatDate";
import { Arrow } from "shared/components/Arrow";
import { canRunOperations } from "shared/api/authority";
import { refusalMessage } from "shared/api/refusalMessage";
import { useMe } from "shared/api/useMe";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { useToast } from "shared/toast/ToastProvider";
import { stateLabel } from "shared/vocab/workflowState";
import { InfoTip } from "shared/components/Explainer";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useCreateMvbDeclaration, useMvbDeclarations } from "foundation/api/hooks";
import { useJourneySpineStatus } from "shared/journey/useJourney";
import type { MvbDeclaration, MvbStatus } from "shared/api/types";

import { Bdi } from "shared/components/Bdi";
const STATUS_TONE: Record<MvbStatus, string> = {
  draft: "bg-slate-100 text-slate-700",
  open: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-900",
  superseded: "bg-slate-200 text-slate-500",
};

const STATUS_ORDER: MvbStatus[] = ["draft", "open", "approved", "superseded"];

/* ROUND 15 C11 -- the register and /v1/mvb/active disagreed. Approving a
   declaration effective on 1 October marks the one it replaces
   `superseded` at once, with its window closed ON 1 October -- so until
   then the OLD one is still what is in force, which is exactly what
   /active returned. The status column records the ceremony; the window
   records what is in force. The badge now reads the window: a superseded
   declaration whose window has not closed is "in force until", and an
   approved one whose window has not opened is "approved, from". */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
type Windowed = "draft" | "open" | "approved" | "superseded";
function windowedStatus(decl: {
  status: MvbStatus;
  effective_from: string | null;
  effective_to: string | null;
}): Windowed {
  const today = todayIso();
  if (decl.status === "superseded" && decl.effective_to && decl.effective_to > today) {
    return "approved";
  }
  return decl.status;
}
function windowedLabel(
  decl: {
    status: MvbStatus;
    effective_from: string | null;
    effective_to: string | null;
  },
  t: TFunction,
  language: string,
): string {
  const today = todayIso();
  if (decl.status === "superseded" && decl.effective_to && decl.effective_to > today) {
    return t("screens.mvbDeclarationsRegister.inForceUntil", {
      to: formatDate(decl.effective_to, language),
    });
  }
  if (
    decl.status === "approved" &&
    decl.effective_from &&
    decl.effective_from > today
  ) {
    return t("screens.mvbDeclarationsRegister.approvedFrom", {
      from: formatDate(decl.effective_from, language),
    });
  }
  return stateLabel(decl.status);
}

export function MvbDeclarationsRegister() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const query = useMvbDeclarations();
  const create = useCreateMvbDeclaration();
  /* A4 -- the PRECONDITION, not just the duplicate.
     ===============================================
     On a bank with zero admitted services, Create draft was enabled,
     took a name, and created the board-signed minimum-viable-bank
     declaration -- locking the one-declaration slot the product has.
     Nothing asked what the declaration would contain, and the
     Resilience Lead's fourth stated fear is doing the ceremony in the
     wrong order and having to unwind it.

     The one-declaration guard B4 built is the right mechanism; it was
     guarding a duplicate and not a precondition, so this adds the
     second to the same control rather than inventing another.

     The count comes from the journey spine because that is the
     product's own shared answer to "where am I", and `foundation/`
     cannot import the twin admission surfaces anyway. Reusing it means
     the register and the ladder cannot disagree about what "admitted"
     means -- the disagreement A3 was about. */
  const spine = useJourneySpineStatus();
  const admittedStep = spine.data?.steps.find((s) => s.key === "ibs_admission");
  const admittedCount = admittedStep?.count ?? null;
  // Null while the spine is loading or unavailable: an unknown count
  // must not be treated as zero, or a slow request becomes a refusal.
  const nothingToDeclare = admittedCount === 0;
  const me = useMe();
  // The seat that RUNS the operation, which is the same authority the
  // candidates register's declare-from-tier-set already carries.
  const canDraft = canRunOperations(me.data);
  // The declaration occupying the one in-progress slot, if any. The
  // register already has every row; the rule is about which of them is
  // in flight, so it is answered here rather than fetched again.
  const inProgress = (query.data ?? []).find(
    (d) => d.status === "draft" || d.status === "open",
  );
  // Round 18 G22 -- the one in force today, which a new one supersedes.
  const today = todayIso();
  const inForce = (query.data ?? []).find(
    (d) =>
      d.status === "approved" &&
      d.effective_from != null &&
      d.effective_from <= today &&
      (d.effective_to == null || d.effective_to >= today),
  );
  const [draftName, setDraftName] = useState("");

  const grouped: Record<MvbStatus, MvbDeclaration[]> = {
    draft: [],
    open: [],
    approved: [],
    superseded: [],
  };
  for (const decl of query.data ?? []) {
    grouped[decl.status].push(decl);
  }

  return (
    <div data-testid="mvb-register" className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("screens.mvbDeclarationsRegister.mvbDeclarations")}
          {/* F2: the page describes the ceremony but never says what
              two signatures actually buy. */}
          <InfoTip termKey="dual_signoff" />
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {t("screens.mvbDeclarationsRegister.theBankAposSMinimumViableBank")}
        </p>
        {/* P272.2 (UX-2 J2-7): reverse link back to the candidate scoring
            that feeds these declarations -- the two were mutually blind. */}
        <Link
          to="/twin/mvb-candidates"
          data-testid="mvb-register-candidates-link"
          className="mt-1 inline-block text-xs font-medium text-brand-brassDark hover:underline"
        >
          {t("screens.mvbDeclarationsRegister.larrScoreMvbCandidates")}
        </Link>
      </header>

      {/* A1 -- the button rendered for every seat that can READ the
          register, which includes the CRO and the business owner,
          because the read gate is deliberately wide (they are the
          people a ceremony waits on). Drafting is the Resilience
          Lead's act; signing is theirs. Offering a verb to a seat that
          cannot use it is what produced the silent 403. */}
      {canDraft ? (
        <form
          data-testid="mvb-create-form"
          className="flex items-end gap-2 rounded-md border border-slate-200 bg-white p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!draftName.trim()) return;
            const named = draftName.trim();
            create.mutate(
              { name: named },
              {
                // Round 9 E6 -- the row appeared in a list below the
                // fold and the box emptied, which is the same thing
                // the screen does when a submission is REFUSED. The
                // one-at-a-time rule makes drafting consequential:
                // this draft is now the one in flight.
                onSuccess: () => {
                  setDraftName("");
                  toast.push({
                    tone: "success",
                    message: t("screens.mvbDeclarationsRegister.draftCreated", {
                      name: named,
                    }),
                  });
                },
              },
            );
          }}
        >
          {/* B2 -- state the rule before she types, not after she is
              refused. The one-at-a-time rule was enforced on submit and
              stated nowhere, so the first anyone learned of it was a
              conflict on a name they had already composed. When one IS
              in flight the form says which, and links to it. */}
          {inProgress ? (
            <p
              data-testid="mvb-in-progress-notice"
              className="w-full text-xs text-slate-600"
            >
              {t("screens.mvbDeclarationsRegister.oneAtATimeBlocked")}{" "}
              <Link
                to={`/foundation/mvb/${inProgress.id}`}
                className="font-medium text-brand-brassDark hover:underline"
              >
                <Bdi>{inProgress.name}</Bdi>
              </Link>
            </p>
          ) : (
            <p
              data-testid="mvb-one-at-a-time"
              className="w-full text-xs text-slate-500"
            >
              {t("screens.mvbDeclarationsRegister.oneAtATime")}
              {inForce && (
                <>
                  {" "}
                  <span data-testid="mvb-supersedes">
                    {t("screens.mvbDeclarationsRegister.supersedes", {
                      name: inForce.name,
                    })}
                  </span>
                </>
              )}
            </p>
          )}
          <label className="flex-1">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              {t("screens.mvbDeclarationsRegister.newDraftName")}
            </span>
            <input
              dir="auto"
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="MVB declaration v1.0"
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
              data-testid="mvb-draft-name-input"
            />
          </label>
          {/* B4 -- the control refuses before the click, and says why.
              It stayed enabled with no title while the rule was
              enforced on submit, so the first anyone learned of it was
              a 409 on a name they had already composed. A disabled
              control with no reason is its own dead end, hence the
              title. */}
          <button
            type="submit"
            disabled={
              !draftName.trim() ||
              create.isPending ||
              Boolean(inProgress) ||
              nothingToDeclare
            }
            /* ROUND 13 H -- every reason, not two of four. The title
               covered the in-progress declaration and the empty
               estate and left the other two undefined, so on a fresh
               form -- where the name box is empty, which is the
               ordinary state -- the button was disabled carrying
               `title=None`. That is the standard round 12 wrote for
               itself after finding exactly this on the empty tenant. */
            title={
              inProgress
                ? t("screens.mvbDeclarationsRegister.blockedTitle", {
                    name: inProgress.name,
                  })
                : nothingToDeclare
                  ? t("screens.mvbDeclarationsRegister.nothingToDeclareTitle")
                  : !draftName.trim()
                    ? t("screens.mvbDeclarationsRegister.needsNameTitle")
                    : create.isPending
                      ? t("screens.mvbDeclarationsRegister.creatingTitle")
                      : undefined
            }
            data-testid="mvb-create-submit"
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:bg-slate-400"
          >
            {t("screens.mvbDeclarationsRegister.createDraft")}
          </button>
          {nothingToDeclare && (
            <p
              data-testid="mvb-nothing-to-declare"
              className="w-full text-xs text-slate-600"
            >
              {t("screens.mvbDeclarationsRegister.nothingToDeclare")}{" "}
              <Link
                to="/twin/ibs-admission"
                className="font-semibold text-brand-brassDark underline underline-offset-2"
              >
                {t("screens.mvbDeclarationsRegister.nothingToDeclareCta")} <Arrow />
              </Link>
            </p>
          )}
          {create.isError && (
            <p data-testid="mvb-create-error" className="w-full text-xs text-rag-red">
              {refusalMessage(t, create.error)}
            </p>
          )}
        </form>
      ) : null}

      {query.isLoading && (
        <div className="rounded bg-slate-50 p-3 text-sm text-slate-600">
          {t("screens.mvbDeclarationsRegister.loadingRegister")}
        </div>
      )}
      {query.error && (
        <div className="rounded bg-rose-50 p-3 text-sm text-rose-900">
          {t("screens.mvbDeclarationsRegister.failedToLoadMvbDeclarations")}{" "}
          {query.error.message}
        </div>
      )}

      {STATUS_ORDER.map((status) => {
        const rows = grouped[status];
        if (rows.length === 0) return null;
        return (
          <section
            key={status}
            data-testid={`mvb-status-group-${status}`}
            className="space-y-2"
          >
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {status} ({rows.length})
            </h2>
            <ul className="space-y-1">
              {rows.map((decl) => (
                <li
                  key={decl.id}
                  data-testid="mvb-register-row"
                  data-status={decl.status}
                  data-declaration-id={decl.id}
                  className="flex items-center justify-between gap-3 rounded border border-slate-200 bg-white px-3 py-2"
                >
                  <Link
                    to={`/foundation/mvb/${decl.id}`}
                    className="flex-1 truncate font-medium text-slate-900 hover:underline"
                  >
                    {decl.name}
                  </Link>
                  <span
                    data-testid="mvb-register-state"
                    className={`rounded px-2 py-0.5 text-[11px] font-medium ${
                      STATUS_TONE[windowedStatus(decl)]
                    }`}
                  >
                    {windowedLabel(decl, t, i18n.language)}
                  </span>
                  {/* C2 -- four rows all called "MVB v1.0" with nothing
                      to tell them apart. Say when each was raised. */}
                  <span
                    data-testid="mvb-register-raised"
                    className="text-[11px] text-slate-500 tabular-nums"
                  >
                    {t("screens.mvbDeclarationsRegister.raised", {
                      when: formatDate(decl.created_at, i18n.language),
                    })}
                  </span>
                  {/* Round 14 C2 -- who put it forward and who signed it,
                      on the row; and the window it held, not a bare date. */}
                  <span
                    data-testid="mvb-register-people"
                    className="text-[11px] text-slate-500"
                  >
                    {decl.cro_signoff_name && decl.ae_signoff_name
                      ? t("screens.mvbDeclarationsRegister.signedBy", {
                          cro: decl.cro_signoff_name,
                          ae: decl.ae_signoff_name,
                        })
                      : decl.submitter_name
                        ? t("screens.mvbDeclarationsRegister.submittedBy", {
                            who: decl.submitter_name,
                          })
                        : null}
                  </span>
                  {decl.effective_from && (
                    <span
                      data-testid="mvb-register-window"
                      className="text-[11px] text-slate-500 tabular-nums"
                    >
                      {decl.effective_to
                        ? t("screens.mvbDeclarationsRegister.window", {
                            from: formatDate(decl.effective_from, i18n.language),
                            to: formatDate(decl.effective_to, i18n.language),
                          })
                        : t("screens.mvbDeclarationsRegister.from", {
                            from: formatDate(decl.effective_from, i18n.language),
                          })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {query.data && query.data.length === 0 && (
        <div
          data-testid="mvb-empty-state"
          className="rounded border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600"
        >
          {t("screens.mvbDeclarationsRegister.noMvbDeclarationsYetCreateADraft")}
        </div>
      )}
    </div>
  );
}
